<template>
    <div class="card">
        <div class="card-header pb-0">
            <div class="row align-top">
                <div class="col-md-3">
                    <h6>Categorías de Producto</h6>
                </div>
                <div class="col-md-7">
                    <div class="input-group">
                        <span class="input-group-text text-body">
                            <i class="fas fa-search" aria-hidden="true"></i>
                        </span>
                        <input
                            type="text"
                            class="form-control"
                            v-model="searchTerm"
                            placeholder="Buscar por nombre de categoría..."
                        />
                    </div>
                </div>
                <div class="col-md-2">
                    <argon-button
                        class="mt-0"
                        color="success"
                        fullWidth
                        size="xs"
                        @click="toCreate"
                    >
                        Agregar Nueva
                    </argon-button>
                </div>
            </div>
        </div>

        <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
                <table class="table align-items-center mb-0">
                    <thead>
                        <tr>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">
                                ID
                            </th>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                Nombre
                            </th>
                            <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                Estado
                            </th>
                            <th class="text-secondary opacity-7">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="isLoadingCiaConfig">
                            <td colspan="4" class="text-center py-4 text-muted">Cargando configuración...</td>
                        </tr>
                        <tr v-else-if="filteredCategories.length === 0">
                            <td colspan="4" class="text-center py-4">
                                {{ searchTerm ? 'No hay coincidencias con la búsqueda.' : 'No se encontraron categorías.' }}
                            </td>
                        </tr>

                        <tr v-for="(category, index) in filteredCategories" :key="index">
                            <td class="ps-4 text-xs font-weight-bold">{{ category.id }}</td>
                            <td class="text-sm">
                                <p class="mb-0 text-sm">{{ category.name }}</p>
                            </td>
                            <td class="align-middle text-center text-sm">
                                <span
                                    :class="{'badge bg-success': category.state === 'ACTIVE', 'badge bg-danger': category.state === 'INACTIVE'}"
                                    class="font-weight-bold"
                                >
                                    {{ category.state }}
                                </span>
                            </td>
                            <td class="align-middle">
                                <a
                                    @click="toEdit(category.id)"
                                    class="btn bg-success mx-2"
                                    data-toggle="tooltip"
                                    data-original-title="Editar categoría"
                                ><i class="fa fa-pen text-white text-sm opacity-10"></i></a>

                                <a
                                    @click="deleteCategory(category.id, category.name)"
                                    class="btn bg-danger mx-2"
                                    data-toggle="tooltip"
                                    data-original-title="Eliminar categoría"
                                ><i class="fa fa-trash text-white text-sm opacity-10"></i></a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import { useCompanyConfig } from '@/composables/useCompanyConfig';
import axios from "axios";
import Swal from 'sweetalert2';

export default {
    name: "CategoriesTable",
    components: { ArgonButton },
    // 🛑 Recibimos la lista de categorías desde el componente padre
    props: {
        categories: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            searchTerm: null,
            // Las propiedades de moneda no son relevantes para esta tabla, pero se mantienen si el setup las requiere
            currency: 'USD',
            symbol: '$'
        };
    },
    setup() {
        // Obtenemos la configuración de la compañía
        const { companyConfig, isLoadingCiaConfig, fetchCompanyConfig } = useCompanyConfig();
        fetchCompanyConfig();
        return { companyConfig ,isLoadingCiaConfig};
    },
    computed: {
        filteredCategories() {
            // Filtro local basado solo en el campo 'name'
            if (!this.searchTerm) {
                return this.categories;
            } else {
                const term = this.searchTerm.toString().toLowerCase();
                return this.categories.filter(item => {
                    return item.name.toString().toLowerCase().includes(term);
                });
            }
        }
    },
    watch: {
        // Opcional: Si se necesita la moneda para algo en el futuro
        companyConfig(newVal) {
            if (newVal) {
                this.currency = newVal.currency_code || 'USD';
                this.symbol = newVal.currency_symbol || '$';
            }
        },
    },
    methods: {
        toCreate() {
            // Navega a la vista de creación
            this.$router.push('/settings/categories/create');
        },
        toEdit(categoryId) {
            // Navega a la vista de edición
            this.$router.push(`/settings/categories/view/${categoryId}`);
        },
        deleteCategory(categoryId, categoryName) {
            const token = localStorage.getItem("accessToken");
            const config = {
                headers: { 'Authorization': `Bearer ${token}` }
            };

            Swal.fire({
                title: `¿Eliminar categoría ${categoryName}?`,
                text: '¡Esta acción no se puede deshacer! Asegúrate de que no haya productos asociados.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            }).then((result) => {
                if (result.isConfirmed) {
                    // Endpoint genérico para DELETE: /products_category/:id
                    axios.delete(this.$store.state.apiURL + 'products_category/' + categoryId, config)
                    .then(() => {
                        Swal.fire({
                            title: 'Categoría eliminada',
                            text: 'La categoría fue eliminada correctamente.',
                            icon: 'success',
                            confirmButtonColor: '#0b9257'
                        }).then(() => {
                            // 🛑 CRÍTICO: Emitir evento para que el componente padre recargue la lista
                            this.$emit('category-deleted');
                        });
                    })
                    .catch((error) => {
                        console.error('Error al eliminar categoría:', error.response || error);
                        Swal.fire({
                            title: 'Error',
                            text: 'No se pudo eliminar la categoría. Podría estar en uso por algún producto.',
                            icon: 'error',
                            confirmButtonColor: '#d33'
                        });
                    });
                }
            });
        },
    },
};
</script>